import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import TextHighlight from "./TextHighlight";
import Lnk from "./Lnk";
import useDebounce from "../../hook/useDebounce";
import useEffectWithoutLoad from "../../hook/useEffectWithoutLoad";

const collator = new Intl.Collator("en", {
  numeric: true,
  sensitivity: "base",
});
const rowsPerPageOptions = [10, 20, 50, 100, 500, 1000];

const Table = (props) => {
  const [debounce] = useDebounce(200);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [filterMode, setFilterMode] = useState(""); // dropdown filter
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [sortDirection, setSortDirection] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedColumnIndex, setSelectedColumn] = useState(
    props.cols.indexOf(props.cols.find((col) => col["sortCol"] != undefined))
  );
  const selectedColumn = props.cols[selectedColumnIndex];
  const [checkValues, setCheckValues] = useState([]);
  const [rows, setRows] = useState(props.rows);
  const [filtered, setFiltered] = useState([]);
  const [sorted, setSorted] = useState([]);
  const [page, setPage] = useState([]);
  const [pageCount, setPageCount] = useState([]);

  const sort = (rows, selectedColumn, sortDirection) => {
    if (selectedColumn == undefined) return rows;

    return [...rows].sort((aKey, bKey) => {
      const a = props.rows[aKey];
      const b = props.rows[bKey];
      let aString = a[selectedColumn.sortCol]?.toString();
      let bString = b[selectedColumn.sortCol]?.toString();
      if (!aString) return sortDirection * -1;
      if (!bString) return sortDirection;
      return sortDirection * collator.compare(aString, bString);
    });
  };

  useEffect(() => {
    setRows(props.rows);
  }, [props.rows]);

  // FILTER FUNCTION - handles blank, error, and keyword search
  /*const filter = (rows, searchKeyword, filterMode) => {
    // If "blank" filter is selected
    if (filterMode === "blank") {
      return rows.filter((rowKey) => {
        const row = props.rows[rowKey];
        const target = row?.target?.toString().trim();
        return !target; // empty or whitespace-only target
      });
    }

    // If "error" filter is selected
    if (filterMode === "error") {
      return rows.filter((rowKey) => {
        const row = props.rows[rowKey];
        const v = row?.hasErrors;

        // normalize shapes:
        if (Array.isArray(v)) return v.length > 0; // e.g. a list of errors
        if (typeof v === "number") return v > 0; // 1, 2, ...
        if (typeof v === "string")
          return v === "1" || v === "true" || v === "TRUE";
        return !!v; // booleans / other truthy
      });
    }

    // Normal keyword search (when filterMode is "")
    if (!searchKeyword) return rows;
    const lwrCaseSubstrings = searchKeyword
      .toLowerCase()
      .split(" ")
      .map((word) => word);

    return rows.filter((rowKey) => {
      const row = props.rows[rowKey];
      const words = [];
      Object.values(row).forEach((colVal) => {
        if (!colVal) colVal = "";
        return colVal
          .toString()
          .toLowerCase()
          .split(" ")
          .forEach((word) => words.push(word));
      });
      return lwrCaseSubstrings.every((i) =>
        words.some((word) => word.includes(i))
      );
    });
  };*/

  // FILTER FUNCTION - handles blank, error, completed, comments and keyword search
  const filter = (rows, searchKeyword, filterMode) => {
    const normalize = (v) => {
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === "number") return v > 0;
      if (typeof v === "string")
        return (
          v === "1" ||
          v === "true" ||
          v === "TRUE" ||
          v.toLowerCase?.() === "true"
        );
      return !!v;
    };

    // 🔹 Only rows with errors
    if (filterMode === "error") {
      return rows.filter((rowKey) => {
        const row = props.rows[rowKey];
        return normalize(row?.hasErrors);
      });
    }

    // 🔹 Only rows WITHOUT errors
    if (filterMode === "noError") {
      return rows.filter((rowKey) => {
        const row = props.rows[rowKey];
        return !normalize(row?.hasErrors);
      });
    }

    // 🔹 Only rows with blank target
    if (filterMode === "blank") {
      return rows.filter((rowKey) => {
        const row = props.rows[rowKey];
        const target = row?.target?.toString().trim();
        return !target; // empty or whitespace-only
      });
    }

    // 🔹 Only rows with NON-blank target
    if (filterMode === "notBlank") {
      return rows.filter((rowKey) => {
        const row = props.rows[rowKey];
        const target = row?.target?.toString().trim();
        return !!target;
      });
    }

    // 🔹 Confirmed (completed)
    if (filterMode === "completed") {
      return rows.filter((rowKey) => {
        const row = props.rows[rowKey];
        return normalize(row?.completed);
      });
    }

    // 🔹 Not confirmed (not completed)
    if (filterMode === "notCompleted") {
      return rows.filter((rowKey) => {
        const row = props.rows[rowKey];
        return !normalize(row?.completed);
      });
    }

    // Has comment
    if (filterMode === "hasComment") {
      return rows.filter((rowKey) => {
        const row = props.rows[rowKey];
        return !!row?.hasComment; // true if rowData.hasComment === true
      });
    }

    // No comment
    if (filterMode === "noComment") {
      return rows.filter((rowKey) => {
        const row = props.rows[rowKey];
        return !row?.hasComment; // false or undefined → "no comment"
      });
    }

    // 🔹 Normal keyword search (when no special filter)
    if (!searchKeyword) return rows;

    const lwrCaseSubstrings = searchKeyword
      .toLowerCase()
      .split(" ")
      .map((word) => word);

    return rows.filter((rowKey) => {
      const row = props.rows[rowKey];
      const words = [];
      Object.values(row).forEach((colVal) => {
        if (!colVal) colVal = "";
        return colVal
          .toString()
          .toLowerCase()
          .split(" ")
          .forEach((word) => words.push(word));
      });
      return lwrCaseSubstrings.every((i) =>
        words.some((word) => word.includes(i))
      );
    });
  };

  const slice = (rows, currentPage, rowsPerPage) => {
    let minRowIndex = (currentPage - 1) * rowsPerPage;
    let maxRowIndex = minRowIndex + rowsPerPage;
    return rows.slice(minRowIndex, maxRowIndex);
  };

  const onCheck = (e) => {
    const checkValue = e.target.getAttribute("checkValue");
    if (!checkValue) return;

    if (e.target.checked) {
      setCheckValues([...checkValues, checkValue]);
    } else {
      let array = [...checkValues];
      array.splice(array.indexOf(checkValue), 1);
      setCheckValues(array);
    }
  };

  useEffect(() => {
    if (props.newCheckValues) props.newCheckValues(checkValues);
  }, [checkValues]);

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  // Recalculate on row changes, searchKeyword changes or filterMode changes
  useEffect(() => {
    const _filtered = filter(
      Object.keys(props.rows),
      searchKeyword,
      filterMode
    );
    const _sorted = sort(_filtered, selectedColumn, sortDirection);
    const _pageCount = Math.ceil(_sorted.length / rowsPerPage);
    const _currentPage = clamp(currentPage, 1, _pageCount || 1);
    const _page = slice(_sorted, _currentPage, rowsPerPage);

    setFiltered(_filtered);
    setSorted(_sorted);
    setPage(_page);
    setPageCount(_pageCount);
    setCurrentPage(_currentPage);
    setCheckValues([]);
  }, [props.rows, searchKeyword, filterMode]);

  // Sort changed
  useEffect(() => {
    const _sorted = sort(filtered, selectedColumn, sortDirection);
    const _pageCount = Math.ceil(_sorted.length / rowsPerPage);
    const _page = slice(_sorted, currentPage, rowsPerPage);
    setSorted(_sorted);
    setPage(_page);
    setPageCount(_pageCount);
  }, [sortDirection, selectedColumnIndex]);

  // Rows per page / page change
  useEffect(() => {
    const _pageCount = Math.ceil(sorted.length / rowsPerPage);
    const _currentPage = clamp(currentPage, 1, _pageCount || 1);
    const _page = slice(sorted, _currentPage, rowsPerPage);
    setPage(_page);
    setPageCount(_pageCount);
    setCurrentPage(_currentPage);
  }, [rowsPerPage, currentPage, sorted]);

  const headerClicked = (key) => {
    if (key === selectedColumnIndex) setSortDirection(sortDirection * -1);
    setSelectedColumn(key);
  };

  const onNavInput = (e) => {
    let value = parseInt(e.target.value);
    if (value < 1) value = 1;
    if (value > pageCount) value = pageCount;
    if (isNaN(value)) value = null;
    setCurrentPage(value);
  };

  const prevPage = () => {
    if (currentPage - 1 < 1) return;
    setCurrentPage(currentPage - 1);
  };

  const nextPage = () => {
    if (currentPage + 1 > pageCount) return;
    setCurrentPage(currentPage + 1);
  };

  const renderData = (data, options) => {
    if (options.hidden) return;
    if (options.highlight !== false && typeof data === "string") {
      const highlight = (
        <TextHighlight
          text={data}
          highlight={[...searchKeyword.split(" ")]}
          className="highlight__table-search"
        />
      );
      if (options.here) {
        return (
          <Data key={options.key}>
            <Lnk here={options.here}>{highlight}</Lnk>
          </Data>
        );
      }
      return <Data key={options.key}>{highlight}</Data>;
    }
    return <Data key={options.key}>{data}</Data>;
  };

  const renderTextareaData = (value, options = {}) => {
    if (options.hidden) return;
    return (
      <Data className={"table__data--textarea"} key={options.key}>
        <TextArea value={value} onInput={options.onInput} />
      </Data>
    );
  };

  let rowsToRender;

  if (props.customRow != true) {
    rowsToRender = page.map((rowKey) => {
      const row = props.rows[rowKey];
      if (!row) return;
      return (
        <Row {...(props.getRowAttributes ? props.getRowAttributes(row) : [])}>
          {props.checkbox == true ? (
            <Data>
              <input
                checkValue={props.getCheckValue(row)}
                checked={checkValues.includes(props.getCheckValue(row))}
                onChange={onCheck}
                className="checkbox"
                type="checkbox"
              />
            </Data>
          ) : (
            ""
          )}
          {props.renderRow(row, renderData, { renderTextareaData })}
        </Row>
      );
    });
  } else {
    rowsToRender = page.map((rowKey) => {
      const row = props.rows[rowKey];
      if (!row) return;
      return props.renderRow(row, renderData, { renderTextareaData });
    });
  }

  return (
    <div className="table-container">
      {props.title || props.titleContent ? (
        <div className="table__title-container">
          <span className="table__title">{props.title}</span>
          <span className="table__title-content">{props.titleContent}</span>
        </div>
      ) : (
        ""
      )}

      <table className={"table " + props.className}>
        <thead>
          <tr>
            {props.checkbox == true ? <th className="table__header"></th> : ""}
            {props.cols.map((col, index) => {
              if (col.hidden) return;
              let selectedClass =
                index === selectedColumnIndex ? "table__header--selected" : "";
              return (
                <th
                  key={index}
                  className={`table__header ${selectedClass} ${col.class}`}
                  onClick={() => {
                    if (col.sortCol !== undefined) {
                      headerClicked(index);
                    }
                  }}
                >
                  {col.name}
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          <tr className="table__row">
            <td
              className="table__data table__data--search"
              colSpan={props.cols.length + 1}
            >
              <input
                className="table__search input"
                type="text"
                placeholder="Search keyword..."
                onChange={(e) => {
                  const value = e.target.value;
                  debounce(() => setSearchKeyword(value));
                }}
              />

              {/* FILTER DROPDOWN */}
              <select
                className="table__row-select select select--secondary"
                onChange={(e) => {
                  setFilterMode(e.target.value);
                  if (e.target.value !== "") {
                    setSearchKeyword(""); // Clear search when using special filters
                  }
                }}
                style={{
                  right: "90px",
                }}
                value={filterMode}
              >
                <option value="">All</option>

                {/* Errors */}
                <option value="error">Errors</option>
                <option value="noError">No errors</option>

                {/* Blank / not blank */}
                <option value="blank">Blank target</option>
                <option value="notBlank">Not blank</option>

                {/* Confirmed / not confirmed */}
                <option value="completed">Confirmed</option>
                <option value="notCompleted">Not confirmed</option>

                {/* Comments */}
                <option value="hasComment">With comment</option>
                <option value="noComment">Without comment</option>
              </select>

              {/* ROWS PER PAGE DROPDOWN */}
              <select
                className="table__row-select select select--secondary"
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
                value={rowsPerPage}
              >
                {rowsPerPageOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </td>
          </tr>

          {page.length > 0 ? (
            rowsToRender
          ) : (
            <tr className="table__row">
              <td className="table__data">
                <div className="spinner" style={{ margin: "0px" }} />
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {pageCount > 1 && (
        <div className="table__nav-container">
          <button
            className={
              "button table__nav-button " +
              (currentPage == 1 ? "table__nav-button--selected" : "")
            }
            onClick={() => setCurrentPage(1)}
          >
            1
          </button>
          <button className="button table__nav-button" onClick={prevPage}>
            <i className="fas fa-chevron-left" aria-hidden="true"></i>
          </button>
          <input
            className="input table__nav-input"
            onChange={onNavInput}
            onSelect={(e) => e.target.select}
            value={currentPage}
          />
          <button className="button table__nav-button" onClick={nextPage}>
            <i className="fas fa-chevron-right" aria-hidden="true"></i>
          </button>
          <button
            className={
              "button table__nav-button " +
              (currentPage == pageCount ? "table__nav-button--selected" : "")
            }
            onClick={() => setCurrentPage(pageCount)}
          >
            {pageCount}
          </button>
        </div>
      )}
    </div>
  );
};

Table.propTypes = {
  cols: PropTypes.array.isRequired,
  rows: PropTypes.object.isRequired,
  title: PropTypes.string,
  titleContent: PropTypes.string,
};

const Row = (props) => (
  <tr className={"table__row " + props.className}>{props.children}</tr>
);
const Data = (props) => (
  <td className={"table__data " + props.className}>{props.children}</td>
);

const TextArea = React.memo(
  (props) => {
    const ref = useRef();
    const [value, setValue] = useState();
    useEffect(() => setValue(props.value), [props.value]);
    useEffectWithoutLoad(() => {
      if (props.focus) ref.current.focus();
    }, [props.focus]);
    return (
      <>
        <textarea
          spellCheck={props.spellcheck === false ? false : true}
          onFocus={props.onFocus}
          ref={ref}
          onInput={(e) => {
            if (props.onInput) props.onInput(e.target.value);
            setValue(e.target.value);
          }}
          hasFocus={props.focus}
          value={value}
          className={"input table__textarea " + props.className}
          tabIndex="-1"
        ></textarea>
        <div className="table__textarea-expander">{value}.</div>
      </>
    );
  },
  (prev, next) => prev.value === next.value && prev.focus === next.focus
);

export { Table, Row, TextArea };
